/**
 * Reading back a template send.
 *
 * ★ THE STORED TEXT IS AN AUDIT RECORD, NOT A MESSAGE BODY.
 * `describeOutbound` (back/services/meta-whatsapp/outboundDispatcher.js) writes
 * `[template hello_world (en_US)]` — plus the filled parameters — because that
 * string is the evidence of a business-initiated conversation: which template,
 * in which language, with which values. It is the right thing to STORE and the
 * wrong thing to SHOW: an agent reading the thread sees brackets, a snake_case
 * id and a language tag where a sentence should be.
 *
 * Meta does not hand back the approved body text with the message, and this
 * workspace has no endpoint that lists templates, so the body genuinely is not
 * available to render. The honest best is the template's own name in words,
 * with its parameters beneath, behind a short "Template" label — which is also
 * what WhatsApp Manager shows in its logs.
 *
 * Parsed rather than re-stored so every message already in the database reads
 * correctly too.
 */

// `[template <name> (<language>)]` — the language and the trailing parameters
// are both optional. `s` so a multi-line parameter still matches.
const TEMPLATE_RE = /^\[template\s+([^\s\]]+?)(?:\s+\(([^)]*)\))?\](?:\s+([\s\S]*))?$/;

/** "hello_world" → "Hello world". */
const humanise = (name) =>
	String(name)
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^./, (c) => c.toUpperCase());

/**
 * @returns {{name: string, label: string, language: string|null, params: string|null}|null}
 *   null when the text is not a stored template description.
 */
export function parseTemplate(content) {
	const match = TEMPLATE_RE.exec(String(content ?? '').trim());
	if (!match) return null;
	const [, name, language, params] = match;
	return {
		name,
		label: humanise(name),
		language: language?.trim() || null,
		params: params?.trim() || null,
	};
}

/** True when this message is a template send, however it was typed. */
export const isTemplateMessage = (message) =>
	message?.type === 'template' || Boolean(parseTemplate(message?.content));

/**
 * The one-line version, for the conversation list — where the raw description
 * was the whole preview and read as noise.
 */
export function previewText(text) {
	const template = parseTemplate(text);
	if (!template) return text;
	return template.params ? `${template.label}: ${template.params}` : template.label;
}
